import React, { useCallback, useEffect, useState } from 'react';
import { supabaseClient } from '../../../config/supabase';
import { canUseIntegration, INTEGRATION_KEYS, useIntegrationAccess } from '../../../lib/integrations/integrationGate';
import Button3D from '../../ui/Button3D';
import AppLink from '../../ui/AppLink';
import { ALERT_OPTIONS } from '../../../lib/integrations/teamsSettings';
import { PillGroup } from './TeamsControls';
import '../../../styles/teamsIntegration.css';

export async function teamsRequest(client, body) {
    const { data, error } = await client.functions.invoke('teams-mirror', { body });
    if (error) {
        let detail;
        try { detail = await error.context?.json?.(); } catch { /* retain the transport error */ }
        throw new Error(detail?.error || error.message || 'Teams could not be reached.');
    }
    return data;
}

export function teamsWebUrl(value) {
    try {
        const url = new URL(value);
        return url.protocol === 'https:' && ['teams.microsoft.com', 'teams.cloud.microsoft'].includes(url.hostname) ? url.href : null;
    } catch { return null; }
}

// Teams owns its own text-only reply path. No shared SMS/AI/schedule/campaign composer is mounted here.
export default function TeamsTextMirror({ userId, client = supabaseClient, mode = 'team', onOpenClient, initialThreadId }) {
    const access = useIntegrationAccess(INTEGRATION_KEYS.MICROSOFT_TEAMS, userId, client);
    const enabled = canUseIntegration(access);
    const [threads, setThreads] = useState([]);
    const [messages, setMessages] = useState([]);
    const [selected, setSelected] = useState(initialThreadId || '');
    const [filter, setFilter] = useState('all');
    const [health, setHealth] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [text, setText] = useState('');
    const [posted, setPosted] = useState('');
    const [query, setQuery] = useState('');
    const [people, setPeople] = useState([]);
    const [channels, setChannels] = useState([]);
    const [teams, setTeams] = useState([]);
    const [teamId, setTeamId] = useState('');
    const [replyTo, setReplyTo] = useState('');
    const [review, setReview] = useState([]);
    const [counts, setCounts] = useState({});
    const [mentions, setMentions] = useState({});
    const [replyAllowed, setReplyAllowed] = useState(false);
    const [channelMembers, setChannelMembers] = useState([]);

    const run = useCallback(async (action) => {
        setError(''); setBusy(true);
        try { return await action(); }
        catch (failure) { setError(failure.message || 'Teams failed.'); return null; }
        finally { setBusy(false); }
    }, []);
    const load = useCallback(async () => {
        const result = await teamsRequest(client, { action: 'catalog' });
        const { data: state, error: failure } = await client.rpc('teams_mirror_health');
        if (failure) throw failure;
        setHealth(state); setThreads(result.threads || []);
        setCounts(Object.fromEntries((result.threads || []).map(t => [t.id, t.unread_count || 0])));
        setMentions(Object.fromEntries((result.threads || []).map(t => [t.id, t.mentioned === true])));
    }, [client]);
    useEffect(() => {
        if (!enabled) { setThreads([]); setMessages([]); return undefined; }
        let active = true;
        const refresh = () => { if (active) run(load); };
        refresh();
        const timer = setInterval(refresh, 60000);
        return () => { active = false; clearInterval(timer); };
    }, [enabled, run, load]);
    const thread = threads.find(t => t.id === selected);
    useEffect(() => {
        setMessages([]); setReplyAllowed(false); setReplyTo(''); setPosted('');
        if (!enabled || !thread?.picked || thread.error) return undefined;
        let active = true;
        run(async () => {
            const result = await teamsRequest(client, { action: 'messages', thread_id: thread.id });
            if (active) {
                setMessages(result.messages || []);
                setReplyTo(result.messages?.find(m => !m.deleted)?.message_id || '');
                setCounts(c => ({ ...c, [thread.id]: 0 }));
            }
            const { data, error: failure } = await client.rpc('teams_reply_context', { p_thread: thread.id });
            // A view-only refusal is expected, but still visible in the composer.
            if (active) setReplyAllowed(!failure && Boolean(data));
        });
        return () => { active = false; };
    }, [enabled, thread?.id, thread?.picked, thread?.error, thread?.replies_enabled, client, run]);
    if (!enabled) return null;
    const visible = threads.filter(t => mode === 'settings' || (['client', 'client-list'].includes(mode) ? t.kind === 'client' : t.kind !== 'client'))
        .filter(t => filter === 'all' || (filter === 'unread' ? counts[t.id] > 0 : mentions[t.id]));
    const pick = (row, picked, level = row.alert_level || 'mentions') => run(async () => {
        await teamsRequest(client, { action: 'pick', thread_id: row.id, picked, alert_level: level });
        await load();
    });
    const open = (row) => { setSelected(row.id); if (onOpenClient) onOpenClient(row); };
    const send = () => run(async () => {
        const result = await teamsRequest(client, { action: 'send', thread_id: selected, text, reply_to: replyTo });
        setPosted(`Posted to Teams as ${result.posted_as}`); setText('');
        const resultMessages = await teamsRequest(client, { action: 'messages', thread_id: selected });
        setMessages(resultMessages.messages || []);
    });
    const findChannels = () => run(async () => {
        const [result, spaces] = await Promise.all([teamsRequest(client, { action: 'channels' }), client.rpc('team_spaces_list')]);
        if (spaces.error) throw spaces.error;
        setChannels(result.channels || []); setTeams((spaces.data || []).filter(t => t.my_role === 'owner'));
    });
    const showReview = () => run(async () => {
        const result = await client.from('teams_review').select('id,resource,reason,created_at');
        if (result.error) throw result.error;
        setReview(result.data || []);
    });
    if (mode === 'client-list') return <section aria-label="Client Teams conversations">
        {visible.filter(row => row.picked && !row.error).map(row => <Button3D key={row.id} onClick={() => onOpenClient?.(row)}>
            {row.title} · Teams · External {counts[row.id] ? `(${counts[row.id]})` : ''}
        </Button3D>)}
        {error && <p role="alert">{error}</p>}
    </section>;
    return <section className="ia-teams min-w-0 p-3" data-testid="teams-text-mirror">
        <header className="flex flex-wrap items-center gap-2">
            <h2>{mode === 'client' ? 'Client Conversations · Teams' : mode === 'settings' ? 'Pick what mirrors' : 'Team chat'}</h2>
            <Button3D disabled={busy} onClick={() => run(load)}>Refresh Teams</Button3D>
            <AppLink to="/team-spaces">My teams / Team settings</AppLink>
        </header>
        {health && <p role={health.stale ? 'alert' : undefined}>
            Last received: {health.last_received_at ? new Date(health.last_received_at).toLocaleString() : 'No message yet'}.
            {health.stale && ' Teams mirror needs attention. Refresh or reconnect in Settings.'}
            {health.last_error && ` ${health.last_error}`}
        </p>}
        {error && <p role="alert">{error}</p>}
        <nav aria-label="Team chat filters" className="flex gap-2">
            {['all', 'mentions', 'unread'].map(value => <Button3D key={value} aria-pressed={filter === value} onClick={() => setFilter(value)}>
                {value === 'all' ? 'All' : value === 'mentions' ? 'Mentions' : 'Unread'}
            </Button3D>)}
        </nav>
        <div className="grid gap-3 md:grid-cols-2">
            <div>
                {!visible.length && <p>Nothing mirrored here yet. Pick a chat in Teams mirror settings.</p>}
                {['client', 'staff', 'channel'].map(kind => {
                    const rows = visible.filter(t => t.kind === kind);
                    return rows.length ? <section key={kind}><h3>{kind === 'channel' ? 'Channels' : kind === 'staff' ? 'Chats' : 'Client conversations'}</h3>
                        {rows.map(row => <div key={row.id} className="ia-teams__field">
                            <Button3D onClick={() => open(row)} disabled={Boolean(row.error)}>{row.title} {counts[row.id] ? `(${counts[row.id]})` : ''}</Button3D>
                            {row.kind === 'client' && <span>Teams · External</span>}
                            {row.error ? <p role="alert">{row.error}</p> : <>
                                <label><input type="checkbox" checked={row.picked === true} disabled={busy} onChange={e => pick(row, e.target.checked)} /> Mirror this chat</label>
                                <PillGroup options={ALERT_OPTIONS} value={row.alert_level} disabled={busy}
                                    onChange={value => pick(row, row.picked, value)} label={`Alerts for ${row.title}`} />
                            </>}
                        </div>)}
                    </section> : null;
                })}
            </div>
            {thread && !thread.error && <article aria-label="Teams conversation">
                <h3>{thread.title} {thread.kind === 'client' && <span>Teams · External</span>}</h3>
                {thread.contact_id && <AppLink to={`/contacts/${thread.contact_id}`}>Client contact</AppLink>}
                {teamsWebUrl(thread.web_url) && <a href={teamsWebUrl(thread.web_url)} target="_blank" rel="noreferrer">Open in Teams</a>}
                <p>Mirror started · AI never answers Teams threads</p>
                <div aria-live="polite">
                    {messages.map(message => <article key={message.message_id} className="ia-teams__notice">
                        <strong>{message.sender_name}</strong> <time>{new Date(message.sent_at).toLocaleString()}</time>
                        <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{message.deleted ? 'Deleted in Teams' : message.body}</p>
                        {message.has_attachments && <p>File · opens in Teams (attachments are not copied)</p>}
                        {message.reactions?.map((reaction, i) => <span key={i}>{reaction.type} {reaction.user} </span>)}
                        {thread.kind === 'channel' && !message.deleted && <Button3D aria-pressed={replyTo === (message.parent_message_id || message.message_id)} onClick={() => setReplyTo(message.parent_message_id || message.message_id)}>Reply to this message</Button3D>}
                    </article>)}
                </div>
                {thread.kind === 'channel' && <Button3D disabled={busy} onClick={() => run(async () => {
                    const result = await client.rpc('teams_channel_replies', { p_thread: thread.id, p_enabled: !thread.replies_enabled });
                    if (result.error) throw result.error;
                    await load();
                })}>Channel replies: {thread.replies_enabled ? 'On' : 'Off'} (owner only)</Button3D>}
                {thread.kind === 'channel' && <><Button3D disabled={busy} onClick={() => run(async () => {
                    const result = await teamsRequest(client, { action: 'channel-members', thread_id: thread.id }); setChannelMembers(result.members);
                })}>Check channel membership (owner only)</Button3D>
                    {channelMembers.map(member => <p key={member.user_id}>{member.name}: {member.status}</p>)}</>}
                <label>Reply via Teams<textarea value={text} onChange={e => setText(e.target.value)} disabled={!replyAllowed || busy} maxLength={10000} /></label>
                {!replyAllowed && <p>This conversation is view only. Your admin or Team Owner controls replies.</p>}
                <Button3D disabled={busy || !replyAllowed || !text.trim()} onClick={send}>Post to Teams as me</Button3D>
                {posted && <p role="status">{posted} ✓</p>}
            </article>}
        </div>
        <details><summary>New message · Teams</summary>
            <label>Find a person<input value={query} onChange={e => setQuery(e.target.value)} /></label>
            <Button3D disabled={busy || query.trim().length < 2} onClick={() => run(async () => {
                const result = await teamsRequest(client, { action: 'people', query }); setPeople(result.people || []);
            })}>Search directory</Button3D>
            {people.map(person => <Button3D key={person.id} disabled={busy} onClick={() => run(async () => {
                await teamsRequest(client, { action: 'create', email: person.email }); await load();
            })}>{person.name}{person.external ? ' · External' : ''}</Button3D>)}
            <p>Outside people must be allowed by your company. Not an approved domain · use SMS or Email instead.</p>
        </details>
        <details><summary>Link a channel (Team Owner)</summary>
            <Button3D onClick={findChannels} disabled={busy}>Find my Teams channels</Button3D>
            <label>IntelAgent team<select value={teamId} onChange={e => setTeamId(e.target.value)}><option value="">Choose my team</option>
                {teams.map(team => <option key={team.team_id} value={team.team_id}>{team.name}</option>)}</select></label>
            {channels.map(channel => <Button3D key={channel.resource} disabled={busy || !teamId} onClick={() => run(async () => {
                await teamsRequest(client, { action: 'link', team_id: teamId, resource: channel.resource }); await load();
            })}>{channel.title}</Button3D>)}
        </details>
        {access.canManage && <details><summary>Outside sender review list</summary><Button3D onClick={showReview}>Load review list</Button3D>
            {review.map(row => <p key={row.id}>{row.resource}: {row.reason}</p>)}</details>}
    </section>;
}
